import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { FocusOverview } from "../../types";

function StreakDots({ value, target }: { value: number; target: number }) {
  return (
    <div className="flex items-center gap-1" aria-label={`${value} of ${target} correct uses`}>
      {Array.from({ length: target }, (_, i) => (
        <span
          key={i}
          className={`w-2 h-2 rounded-full ${i < value ? "bg-success" : "bg-border-soft"}`}
        />
      ))}
    </div>
  );
}

function Stat({ value, caption, className = "" }: { value: string | number; caption: string; className?: string }) {
  return (
    <div className={`shrink-0 w-16 sm:w-20 text-right ${className}`}>
      <div className="font-display text-2xl font-semibold text-ink tabular-nums leading-none">{value}</div>
      <div className="mt-1.5 text-[0.65rem] uppercase tracking-wider text-text-tertiary">{caption}</div>
    </div>
  );
}

function ExampleButton({ examples }: { examples: { wrong: string; right: string }[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-label="Show examples"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-4 h-4 rounded-full border border-border-soft text-[0.6rem] leading-none text-text-tertiary hover:text-ink hover:border-text-tertiary flex items-center justify-center"
      >
        i
      </button>
      {open && (
        <div role="tooltip" className="absolute left-0 top-6 z-10 w-72 max-w-[80vw] card p-3 shadow-lg flex flex-col gap-2 text-xs leading-relaxed break-words">
          {examples.map((ex, i) => (
            <div key={i}>
              <span className="text-error line-through decoration-error/50">{ex.wrong}</span>
              <span className="text-text-tertiary"> → </span>
              <span className="text-success">{ex.right}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

export default function TopMistakesCard({ data }: { data: FocusOverview }) {
  const navigate = useNavigate();
  if (!data.top.length && !data.recently_mastered.length) return null;

  return (
    <div className="card p-6 animate-rise mb-8">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="min-w-0">
          <h4 className="section-label mb-1">Your top mistakes</h4>
          <p className="text-sm text-text-tertiary">
            Elena works these into your sessions. Say one correctly {data.mastery_target} times without slipping and it's off the list.
          </p>
        </div>
        {data.top.length > 0 && (
          <button className="btn-primary px-5 py-2 text-sm whitespace-nowrap" onClick={() => navigate("/?focus=1")}>
            Focus session
          </button>
        )}
      </div>

      {data.top.length > 0 ? (
        <ol className="flex flex-col divide-y divide-border-soft">
          {data.top.map((item, i) => {
            const examples = item.examples?.length ? item.examples : [{ wrong: item.example_wrong, right: item.example_right }];
            return (
              <li key={item.key} className="py-4 first:pt-0 last:pb-0 flex items-center gap-4 sm:gap-6">
                <span className="font-display text-lg font-semibold text-accent-deep tabular-nums w-5 shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink">{item.label}</span>
                    <ExampleButton examples={examples} />
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-border-soft overflow-hidden" aria-hidden>
                    <div className="h-full rounded-full bg-error/70" style={{ width: `${Math.max(item.share, 3)}%` }} />
                  </div>
                  <div className="sm:hidden text-xs text-text-tertiary mt-1.5">
                    <b className="text-ink font-semibold tabular-nums">{item.count}</b> {item.count === 1 ? "time" : "times"} ·{" "}
                    <b className="text-ink font-semibold tabular-nums">{item.sessions}</b> {item.sessions === 1 ? "session" : "sessions"}
                  </div>
                </div>
                <Stat value={`${item.share}%`} caption="of mistakes" />
                <Stat value={item.count} caption={item.count === 1 ? "time" : "times"} className="hidden sm:block" />
                <Stat value={item.sessions} caption={item.sessions === 1 ? "session" : "sessions"} className="hidden sm:block" />
                <div className="shrink-0">
                  <StreakDots value={Math.min(item.correct_streak, item.mastery_target)} target={item.mastery_target} />
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="text-sm text-text-secondary">No repeated mistakes right now. Nice work.</p>
      )}

      {data.recently_mastered.length > 0 && (
        <div className="mt-5 pt-4 border-t border-border-soft">
          <h4 className="section-label mb-2">Recently nailed</h4>
          <div className="flex flex-wrap gap-2">
            {data.recently_mastered.map((item) => (
              <span
                key={item.key}
                title={`${item.example_wrong} → ${item.example_right}`}
                className="text-xs text-success bg-success/10 border border-success/30 rounded-full px-3 py-1"
              >
                ✓ {item.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
